// Thin request/response wrapper around compressionWorker.worker.ts. Lazily creates the worker
// (native Vite module worker, not the `web-worker` npm polyfill) on first use, and correlates
// responses to requests via an incrementing message id, since a single worker instance may be
// asked to compress/decompress multiple payloads without waiting for earlier ones to finish.
let worker: Worker | null = null;
let nextRequestId = 0;
const pendingRequests = new Map<number, { resolve: (value: any) => void; reject: (reason: unknown) => void }>();

function getWorker(): Worker {
  if (!worker) {
    worker = new Worker(new URL('./compressionWorker.worker.ts', import.meta.url), { type: 'module' });
    worker.onmessage = (event: MessageEvent<{ id: number; result?: unknown; error?: string }>) => {
      const { id, result, error } = event.data;
      const pending = pendingRequests.get(id);
      if (!pending) return;
      pendingRequests.delete(id);
      if (error) pending.reject(new Error(error));
      else pending.resolve(result);
    };
  }
  return worker;
}

function sendRequest<T>(action: 'compress' | 'decompress', payload: unknown): Promise<T> {
  const id = nextRequestId++;
  return new Promise<T>((resolve, reject) => {
    pendingRequests.set(id, { resolve, reject });
    getWorker().postMessage({ id, action, payload });
  });
}

export function compressJson(value: unknown): Promise<Uint8Array> {
  return sendRequest<Uint8Array>('compress', value);
}

export function decompressToJson<T>(bytes: Uint8Array): Promise<T> {
  return sendRequest<T>('decompress', bytes);
}
